import type {
  DatabaseAllEvents,
  EventTraceFn,
} from '@blocksuite/affine-shared/services';
import {
  createRemotePresenceCursorLabelStyle,
  isRenderableRemotePresenceAvatarUrl,
} from '@blocksuite/affine-shared/utils';
import type { DisposableMember } from '@blocksuite/global/disposable';
import { IS_MOBILE } from '@blocksuite/global/env';
import { BlockSuiteError } from '@blocksuite/global/exceptions';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type Clipboard,
  type EventName,
  ShadowlessElement,
  type UIEventHandler,
} from '@blocksuite/std';
import { measureGfxPerformance } from '@blocksuite/std/gfx';
import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';
import { databaseRowCounter } from '@streamlain/database-identity';
import { css, nothing, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';
import { html } from 'lit/static-html.js';

import { dataViewCommonStyle } from './common/css-variable.js';
import type { DataSource } from './data-source/index.js';
import { setDataSourcePerformanceScope } from './data-source/projection-cache.js';
import {
  type DataViewFooterProgress,
  renderDataViewFooterProgress,
} from './footer-progress.js';
import { dataViewI18nT } from './i18n.js';
import {
  type DataViewRemoteAreaSelectionPresence,
  type DataViewRemoteCellPresence,
  type DataViewRemoteRowFocus,
  remoteCellPresenceLabel,
} from './row-focus.js';
import type { DataViewSelection } from './types.js';
import { cacheComputed } from './utils/cache.js';
import { renderUniLit } from './utils/uni-component/index.js';
import type { DataViewUILogicBase } from './view/data-view-base.js';
import type { SingleView } from './view-manager/single-view.js';
import type { DataViewWidget } from './widget/index.js';

let remoteCellCaretPortalOwnerSeed = 0;

export type DataViewRendererConfig = {
  performanceScope: object;
  clipboard: Clipboard;
  onDrag?: (evt: MouseEvent, id: string) => () => void;
  notification: {
    toast: (message: string) => void;
  };
  virtualPadding$: ReadonlySignal<number>;
  headerWidget: DataViewWidget | undefined;
  footerWidget?: DataViewWidget;
  renderHeaderWidget?: boolean;
  renderFooterWidget?: boolean;
  footerProgress$?: ReadonlySignal<DataViewFooterProgress | null>;
  handleEvent: (name: EventName, handler: UIEventHandler) => DisposableMember;
  bindHotkey: (hotkeys: Record<string, UIEventHandler>) => DisposableMember;
  dataSource: DataSource;
  remoteAreaSelections$?: ReadonlySignal<DataViewRemoteAreaSelectionPresence[]>;
  remoteRowFocuses$?: ReadonlySignal<DataViewRemoteRowFocus[]>;
  remoteCellPresences$?: ReadonlySignal<DataViewRemoteCellPresence[]>;
  selection$: ReadonlySignal<DataViewSelection | undefined>;
  preferDesktopRenderer?: boolean;
  setSelection: (selection: DataViewSelection | undefined) => void;
  eventTrace: EventTraceFn<DatabaseAllEvents>;
  detailPanelConfig: {
    openDetailPanel: (
      target: HTMLElement,
      data: {
        view: SingleView;
        rowId: string;
      }
    ) => Promise<void>;
  };
};

export class DataViewRootUILogic {
  private get dataSource() {
    return this.config.dataSource;
  }
  private get viewManager() {
    return this.dataSource.viewManager;
  }
  private createDataViewUILogic(viewId: string): DataViewUILogicBase {
    const view = this.viewManager.viewGet(viewId);
    if (!view) {
      throw new BlockSuiteError(
        BlockSuiteError.ErrorCode.DatabaseBlockError,
        `View ${viewId} not found`
      );
    }

    const pcLogic = view.meta.renderer.pcLogic;
    const mobileLogic = view.meta.renderer.mobileLogic;
    const logic =
      (IS_MOBILE && !this.config.preferDesktopRenderer
        ? mobileLogic
        : pcLogic) ?? pcLogic;

    return new (logic(view))(this, view);
  }
  /**
   * Cache key includes the view mode so that when viewChangeType() changes
   * the mode (e.g. kanban→table), the key changes, the old entry is evicted,
   * and a fresh UI logic with the correct renderer is created automatically.
   */
  private readonly viewKeys$ = computed(() => {
    return this.dataSource.viewDataList$.value.map(
      data => `${data.id}:${data.mode}`
    );
  });
  private readonly views$ = cacheComputed(this.viewKeys$, compositeKey => {
    const viewId = compositeKey.split(':')[0] ?? compositeKey;
    return this.createDataViewUILogic(viewId);
  });
  private readonly viewsMap$ = computed(() => {
    return Object.fromEntries(
      this.views$.list.value.map(logic => [logic.view.id, logic])
    );
  });
  private readonly _uiRef = signal<DataViewRootUI>();

  get selection$() {
    return this.config.selection$;
  }

  get remoteRowFocuses$() {
    return this.config.remoteRowFocuses$;
  }

  get remoteCellPresences$() {
    return this.config.remoteCellPresences$;
  }

  get remoteAreaSelections$() {
    return this.config.remoteAreaSelections$;
  }

  setSelection(selection?: DataViewSelection) {
    this.config.setSelection(selection);
  }

  constructor(public readonly config: DataViewRendererConfig) {
    setDataSourcePerformanceScope(config.dataSource, config.performanceScope);
  }

  get dataViewRenderer() {
    return this._uiRef.value;
  }

  readonly currentViewId$ = computed(() => {
    return this.dataSource.viewManager.currentViewId$.value;
  });

  readonly currentView$ = computed(() => {
    const currentViewId = this.currentViewId$.value;
    if (!currentViewId) {
      return;
    }
    return this.viewsMap$.value[currentViewId];
  });

  focusFirstCell = () => {
    this.currentView$.value?.focusFirstCell();
  };

  openDetailPanel = (ops: {
    view: SingleView;
    rowId: string;
    onClose?: () => void;
  }) => {
    const openDetailPanel = this.config.detailPanelConfig.openDetailPanel;
    const target = this.dataViewRenderer;
    if (openDetailPanel && target) {
      openDetailPanel(target, {
        view: ops.view,
        rowId: ops.rowId,
      })
        .catch(console.error)
        .finally(ops.onClose);
    }
  };

  setupViewChangeListener() {
    let preId: string | undefined = undefined;
    return this.currentViewId$.subscribe(current => {
      if (current !== preId) {
        this.config.setSelection(undefined);
      }
      preId = current;
    });
  }

  render() {
    return html` <affine-data-view-renderer
      ${ref(this._uiRef)}
      .logic="${this}"
    ></affine-data-view-renderer>`;
  }
}

export class DataViewRootUI extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    ${unsafeCSS(dataViewCommonStyle('affine-data-view-renderer'))}
    affine-data-view-renderer {
      background-color: var(--streamlain-v2-layer-background-primary);
      display: contents;
    }

    .data-view-root {
      display: flex;
      flex-direction: column;
      min-height: 0;
      width: 100%;
    }

    .data-view-root [data-data-view-frame-top='true'] {
      flex: 0 0 auto;
      min-height: 0;
    }

    .data-view-root [data-data-view-frame-body='true'] {
      display: block;
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
    }

    .data-view-root [data-data-view-frame-body='true'] > * {
      box-sizing: border-box;
      min-height: 0;
      min-width: 0;
      width: 100%;
    }

    /* Lies over the bottom of the body; the zero-height anchor keeps the
       frame height unchanged. */
    .data-view-root [data-data-view-footer-progress-anchor='true'] {
      flex: 0 0 0;
      height: 0;
      position: relative;
    }

    .data-view-root [data-data-view-footer-progress='true'] {
      background: var(--affine-background-overlay-panel-color);
      border-top: 1px solid var(--affine-border-color);
      bottom: 0;
      color: var(--affine-text-secondary-color);
      font-size: 12px;
      left: 0;
      padding: 6px 12px;
      position: absolute;
      right: 0;
      z-index: 1;
    }

    .data-view-root [data-data-view-footer-progress-track='true'] {
      background: var(--affine-border-color);
      border-radius: 2px;
      height: 4px;
      margin-top: 4px;
      overflow: hidden;
    }

    .data-view-root [data-data-view-footer-progress-bar='true'] {
      background: var(--affine-primary-color);
      height: 100%;
    }

    .data-view-root [data-remote-row-focus] {
      --dv-remote-row-focus-bg: color-mix(
        in srgb,
        var(--dv-remote-row-focus-color) 12%,
        transparent
      );

      outline: 2px solid var(--dv-remote-row-focus-color);
      outline-offset: -2px;
      box-shadow: inset 3px 0 0 var(--dv-remote-row-focus-color);
      background-image: linear-gradient(
        var(--dv-remote-row-focus-bg),
        var(--dv-remote-row-focus-bg)
      );
    }

    .data-view-root [data-bar-row-id][data-remote-row-focus] {
      outline-offset: 2px;
    }

    .data-view-root .dv-remote-row-focus-badges {
      position: absolute;
      top: 4px;
      right: 4px;
      z-index: 30;
      display: flex;
      align-items: center;
      gap: 4px;
      pointer-events: none;
    }

    .data-view-root .dv-remote-row-focus-badge {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      border-radius: 999px;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      line-height: 18px;
      padding: 0 8px;
      box-shadow: 0 1px 4px rgb(0 0 0 / 18%);
    }

    .data-view-root [data-remote-cell-presence] {
      outline: 2px solid var(--dv-remote-cell-presence-color);
      outline-offset: -2px;
      overflow: visible;
      box-shadow: inset 0 0 0 1px var(--dv-remote-cell-presence-color);
      z-index: 20;
    }

    .data-view-root [data-remote-cell-presence-mode='editing'] {
      outline-width: 3px;
    }

    .data-view-root .dv-remote-cell-presence-badges,
    .dv-remote-area-selection-badges {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      pointer-events: none;
    }

    .data-view-root .dv-remote-cell-presence-badges {
      position: absolute;
      top: 4px;
      right: 6px;
      z-index: 40;
    }

    .data-view-root .dv-remote-cell-presence-badges[data-placement='top-left'] {
      right: auto;
      left: 6px;
      flex-direction: row;
    }

    .data-view-root
      .dv-remote-cell-presence-badges[data-placement='top-left']
      .dv-remote-presence-avatar {
      margin-right: var(--dv-remote-presence-avatar-overlap, -6px);
      margin-left: 0;
    }

    .dv-remote-area-selection-badges {
      position: fixed;
      z-index: 9999;
    }

    .dv-remote-presence-avatar {
      width: var(--dv-remote-presence-avatar-size, 20px);
      height: var(--dv-remote-presence-avatar-size, 20px);
      margin-left: var(--dv-remote-presence-avatar-overlap, -6px);
      position: relative;
      overflow: visible;
      border-radius: 50%;
      color: #fff;
      font-size: var(--dv-remote-presence-avatar-font-size, 9px);
      font-weight: 700;
      line-height: 1;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dv-remote-presence-avatar::before {
      content: '';
      position: absolute;
      inset: var(--dv-remote-presence-avatar-ring-inset, -2px);
      border: var(--dv-remote-presence-avatar-ring-width, 2px) solid #fff;
      border-radius: inherit;
      box-sizing: border-box;
      pointer-events: none;
      box-shadow: 0 var(--dv-remote-presence-avatar-shadow-y, 1px)
        var(--dv-remote-presence-avatar-shadow-blur, 4px) rgb(0 0 0 / 20%);
    }

    .dv-remote-presence-avatar-content {
      position: relative;
      z-index: 1;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: inherit;
      background: var(--dv-remote-presence-avatar-color);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dv-remote-presence-avatar-content img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    .data-view-root .dv-remote-cell-caret-presence {
      position: absolute;
      z-index: 45;
      width: 2px;
      pointer-events: none;
    }

    .data-view-root .dv-remote-cell-caret-line {
      width: 2px;
      min-height: 16px;
      border-radius: 999px;
      background: var(--dv-remote-cell-caret-color);
      box-shadow: 0 0 0 1px rgb(255 255 255 / 75%);
    }

    .data-view-root .dv-remote-cell-caret-label {
      pointer-events: none;
    }
  `;

  @property({ attribute: false })
  accessor logic!: DataViewRootUILogic;

  @state()
  accessor currentView: string | undefined = undefined;

  focusFirstCell = () => {
    this.logic.focusFirstCell();
  };

  openDetailPanel = (ops: {
    view: SingleView;
    rowId: string;
    onClose?: () => void;
  }) => {
    this.logic.openDetailPanel(ops);
  };

  private _remoteRowFocusFrame: number | null = null;

  private _remoteRowFocusObserver: MutationObserver | null = null;

  private _applyingRemoteRowFocus = false;

  private readonly _remoteCellCaretPortalOwner = `data-view-remote-cell-caret-${++remoteCellCaretPortalOwnerSeed}`;

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.add(this.logic.setupViewChangeListener());
    this.disposables.add(
      effect(() => {
        this.logic.currentViewId$.value;
        this.logic.remoteAreaSelections$?.value;
        this.logic.remoteRowFocuses$?.value;
        this.logic.remoteCellPresences$?.value;
        this.scheduleRemoteRowFocusRender();
      })
    );

    this._remoteRowFocusObserver = new MutationObserver(mutations => {
      if (this._applyingRemoteRowFocus) {
        return;
      }
      const onlyFocusBadgesChanged = mutations.every(mutation => {
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        return (
          nodes.length > 0 &&
          nodes.every(node => this.isRemoteFocusBadgeNode(node))
        );
      });
      if (!onlyFocusBadgesChanged) {
        this.scheduleRemoteRowFocusRender();
      }
    });
    this._remoteRowFocusObserver.observe(this, {
      childList: true,
      subtree: true,
    });
    const scheduleRemoteRender = () => this.scheduleRemoteRowFocusRender();
    this.addEventListener('scroll', scheduleRemoteRender, {
      capture: true,
      passive: true,
    });
    this.ownerDocument.defaultView?.addEventListener(
      'resize',
      scheduleRemoteRender
    );
    this.disposables.add(() => {
      this._remoteRowFocusObserver?.disconnect();
      this._remoteRowFocusObserver = null;
      if (this._remoteRowFocusFrame !== null) {
        cancelAnimationFrame(this._remoteRowFocusFrame);
        this._remoteRowFocusFrame = null;
      }
      this.clearRemoteRowFocusDecorations();
      this.removeEventListener('scroll', scheduleRemoteRender, {
        capture: true,
      });
      this.ownerDocument.defaultView?.removeEventListener(
        'resize',
        scheduleRemoteRender
      );
    });
  }

  private isRemoteFocusBadgeNode(node: Node) {
    if (!(node instanceof HTMLElement)) {
      return false;
    }
    return (
      node.classList.contains('dv-remote-row-focus-badges') ||
      node.classList.contains('dv-remote-cell-presence-badges') ||
      node.classList.contains('dv-remote-cell-caret-presence') ||
      node.classList.contains('dv-remote-area-selection-overlay') ||
      node.classList.contains('dv-remote-area-selection-badges') ||
      !!node.closest(
        '.dv-remote-row-focus-badges, .dv-remote-cell-presence-badges, .dv-remote-cell-caret-presence, .dv-remote-area-selection-overlay, .dv-remote-area-selection-badges'
      )
    );
  }

  private scheduleRemoteRowFocusRender() {
    if (this._remoteRowFocusFrame !== null) {
      return;
    }
    this._remoteRowFocusFrame = requestAnimationFrame(() => {
      this._remoteRowFocusFrame = null;
      measureGfxPerformance(
        this.logic.config.performanceScope,
        'database-presence-decoration',
        () => this.applyRemoteRowFocusDecorations(),
        () =>
          (this.logic.remoteAreaSelections$?.value.length ?? 0) +
          (this.logic.remoteRowFocuses$?.value.length ?? 0) +
          (this.logic.remoteCellPresences$?.value.length ?? 0)
      );
    });
  }

  private clearRemoteRowFocusDecorations() {
    this.querySelectorAll<HTMLElement>('[data-remote-row-focus]').forEach(
      element => {
        element.removeAttribute('data-remote-row-focus');
        element.removeAttribute('data-remote-row-focus-count');
        element.style.removeProperty('--dv-remote-row-focus-color');
        if (element.dataset.dvRowFocusPositionAdjusted === 'true') {
          element.style.position = '';
          delete element.dataset.dvRowFocusPositionAdjusted;
        }
      }
    );
    this.querySelectorAll('.dv-remote-row-focus-badges').forEach(element =>
      element.remove()
    );
    this.querySelectorAll<HTMLElement>('[data-remote-cell-presence]').forEach(
      element => {
        element.removeAttribute('data-remote-cell-presence');
        element.removeAttribute('data-remote-cell-presence-count');
        element.removeAttribute('data-remote-cell-presence-mode');
        element.style.removeProperty('--dv-remote-cell-presence-color');
        if (element.dataset.dvCellPresencePositionAdjusted === 'true') {
          element.style.position = '';
          delete element.dataset.dvCellPresencePositionAdjusted;
        }
      }
    );
    this.querySelectorAll('.dv-remote-cell-presence-badges').forEach(element =>
      element.remove()
    );
    this.querySelectorAll<HTMLElement>('[data-remote-area-selection]').forEach(
      element => {
        element.removeAttribute('data-remote-area-selection');
        element.removeAttribute('data-remote-area-selection-count');
        element.style.removeProperty('--dv-remote-area-selection-color');
      }
    );
    this.querySelectorAll('.dv-remote-area-selection-badges').forEach(element =>
      element.remove()
    );
    this.ownerDocument
      .querySelectorAll(
        `[data-dv-remote-area-selection-owner="${this._remoteCellCaretPortalOwner}"]`
      )
      .forEach(element => element.remove());
    this.querySelectorAll('.dv-remote-cell-caret-presence').forEach(element =>
      element.remove()
    );
    this.ownerDocument
      .querySelectorAll(
        `[data-dv-remote-cell-caret-owner="${this._remoteCellCaretPortalOwner}"]`
      )
      .forEach(element => element.remove());
  }

  private applyRemoteRowFocusDecorations() {
    this._applyingRemoteRowFocus = true;
    try {
      this.clearRemoteRowFocusDecorations();
      const focuses = this.logic.remoteRowFocuses$?.value ?? [];
      if (focuses.length > 0) {
        const focusGroups = new Map<string, DataViewRemoteRowFocus[]>();
        focuses.forEach(focus => {
          const group = focusGroups.get(focus.rowId) ?? [];
          group.push(focus);
          focusGroups.set(focus.rowId, group);
        });

        focusGroups.forEach((group, rowId) => {
          const targets = this.getRemoteRowFocusTargets(rowId);
          if (targets.length === 0) {
            return;
          }

          const primary = group[0];
          if (!primary) {
            return;
          }
          targets.forEach(target => {
            target.setAttribute('data-remote-row-focus', 'true');
            target.setAttribute(
              'data-remote-row-focus-count',
              `${group.length}`
            );
            target.style.setProperty(
              '--dv-remote-row-focus-color',
              primary.color
            );
          });

          const badgeTarget = targets[0];
          if (badgeTarget) {
            this.renderRemoteRowFocusBadges(badgeTarget, group);
          }
        });
      }

      this.applyRemoteAreaSelectionDecorations();
      this.applyRemoteCellPresenceDecorations();
    } finally {
      this._applyingRemoteRowFocus = false;
    }
  }

  private applyRemoteCellPresenceDecorations() {
    const activeAreaClientIds = new Set(
      (this.logic.remoteAreaSelections$?.value ?? []).map(
        selection => selection.clientId
      )
    );
    const presences = (this.logic.remoteCellPresences$?.value ?? []).filter(
      presence => !activeAreaClientIds.has(presence.clientId)
    );
    if (presences.length === 0) {
      return;
    }

    const presenceGroups = new Map<string, DataViewRemoteCellPresence[]>();
    presences.forEach(presence => {
      const key = `${presence.rowId}:${presence.columnId}`;
      const group = presenceGroups.get(key) ?? [];
      group.push(presence);
      presenceGroups.set(key, group);
    });

    presenceGroups.forEach(group => {
      const primary = group[0];
      if (!primary) {
        return;
      }
      const targets = this.getRemoteCellPresenceTargets(
        primary.rowId,
        primary.columnId
      );
      if (targets.length === 0) {
        return;
      }

      targets.forEach(target => {
        target.setAttribute('data-remote-cell-presence', 'true');
        target.setAttribute(
          'data-remote-cell-presence-count',
          `${group.length}`
        );
        target.setAttribute('data-remote-cell-presence-mode', primary.mode);
        target.style.setProperty(
          '--dv-remote-cell-presence-color',
          primary.color
        );
      });

      const badgeTarget = targets[0];
      if (badgeTarget) {
        this.renderRemoteCellPresenceBadges(badgeTarget, group);
      }
    });
  }

  private applyRemoteAreaSelectionDecorations() {
    const selections = this.logic.remoteAreaSelections$?.value ?? [];
    if (selections.length === 0) {
      return;
    }

    selections.forEach(selection => {
      const targets = this.getRemoteAreaSelectionTargets(selection);
      if (targets.length === 0) {
        return;
      }
      targets.forEach(target => {
        target.setAttribute('data-remote-area-selection', 'true');
        target.setAttribute('data-remote-area-selection-count', '1');
        target.style.setProperty(
          '--dv-remote-area-selection-color',
          selection.color
        );
      });
      this.renderRemoteAreaSelectionOverlay(targets, selection);
    });
  }

  private getRemoteRowFocusTargets(rowId: string) {
    const escapedRowId = this.escapeSelectorValue(rowId);
    const selectors = [
      `data-view-table-row[data-row-id="${escapedRowId}"]`,
      `dv-table-view-cell-container[data-row-id="${escapedRowId}"]`,
      `affine-data-view-kanban-card[data-card-id="${escapedRowId}"]`,
      `[data-label-row-id="${escapedRowId}"]`,
      `[data-bar-row-id="${escapedRowId}"]`,
    ];
    const targets = selectors.flatMap(selector =>
      [...this.querySelectorAll<HTMLElement>(selector)].filter(
        target => !target.closest('.dv-remote-row-focus-badges')
      )
    );
    return [...new Set(targets)];
  }

  private renderRemoteRowFocusBadges(
    target: HTMLElement,
    focuses: DataViewRemoteRowFocus[]
  ) {
    if (getComputedStyle(target).position === 'static') {
      target.style.position = 'relative';
      target.dataset.dvRowFocusPositionAdjusted = 'true';
    }

    const badges = document.createElement('div');
    badges.className = 'dv-remote-row-focus-badges';
    focuses.forEach(focus => {
      const badge = document.createElement('div');
      badge.className = 'dv-remote-row-focus-badge';
      badge.style.backgroundColor = focus.color;
      badge.textContent = focus.userName ?? 'User';
      badges.append(badge);
    });
    target.append(badges);
  }

  private getRemoteCellPresenceTargets(rowId: string, columnId: string) {
    const escapedRowId = this.escapeSelectorValue(rowId);
    const escapedColumnId = this.escapeSelectorValue(columnId);
    const frameSelector = `[data-database-cell-frame="true"][data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"]`;
    const frameTargets = [
      ...this.querySelectorAll<HTMLElement>(frameSelector),
    ].filter(target => !target.closest('.dv-remote-cell-presence-badges'));
    if (frameTargets.length > 0) {
      return [...new Set(frameTargets)];
    }

    const fallbackSelectors = [
      `affine-database-virtual-cell-container[data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"]`,
      `dv-table-view-cell-container[data-row-id="${escapedRowId}"][data-column-id="${escapedColumnId}"]`,
    ];
    const fallbackTargets = fallbackSelectors.flatMap(selector =>
      [...this.querySelectorAll<HTMLElement>(selector)].filter(
        target => !target.closest('.dv-remote-cell-presence-badges')
      )
    );
    return [...new Set(fallbackTargets)];
  }

  private getRemoteAreaSelectionTargets(
    selection: DataViewRemoteAreaSelectionPresence
  ) {
    const rowIds = new Set(selection.rowIds);
    const columnIds = new Set(selection.columnIds);
    const frameTargets = [
      ...this.querySelectorAll<HTMLElement>(
        '[data-database-cell-frame="true"][data-row-id][data-column-id]'
      ),
    ].filter(
      target =>
        !!target.dataset.rowId &&
        !!target.dataset.columnId &&
        rowIds.has(target.dataset.rowId) &&
        columnIds.has(target.dataset.columnId)
    );
    if (frameTargets.length > 0) {
      return [...new Set(frameTargets)];
    }

    const fallbackTargets = [
      ...this.querySelectorAll<HTMLElement>(
        'affine-database-virtual-cell-container[data-row-id][data-column-id], dv-table-view-cell-container[data-row-id][data-column-id]'
      ),
    ].filter(
      target =>
        !!target.dataset.rowId &&
        !!target.dataset.columnId &&
        rowIds.has(target.dataset.rowId) &&
        columnIds.has(target.dataset.columnId)
    );
    return [...new Set(fallbackTargets)];
  }

  private renderRemoteAreaSelectionOverlay(
    targets: HTMLElement[],
    selection: DataViewRemoteAreaSelectionPresence
  ) {
    const rects = targets
      .map(target => target.getBoundingClientRect())
      .filter(rect => rect.width > 0 && rect.height > 0);
    if (rects.length === 0) {
      return;
    }
    const left = Math.min(...rects.map(rect => rect.left));
    const top = Math.min(...rects.map(rect => rect.top));
    const right = Math.max(...rects.map(rect => rect.right));
    const bottom = Math.max(...rects.map(rect => rect.bottom));
    const overlay = this.ownerDocument.createElement('div');
    overlay.className = 'dv-remote-area-selection-overlay';
    overlay.dataset.dvRemoteAreaSelectionOwner =
      this._remoteCellCaretPortalOwner;
    Object.assign(overlay.style, {
      background: `color-mix(in srgb, ${selection.color} 8%, transparent)`,
      border: `2px solid ${selection.color}`,
      borderRadius: '2px',
      boxSizing: 'border-box',
      height: `${bottom - top}px`,
      left: `${left}px`,
      pointerEvents: 'none',
      position: 'fixed',
      top: `${top}px`,
      width: `${right - left}px`,
      zIndex: '9998',
    });

    const badge = this.ownerDocument.createElement('div');
    badge.className = 'dv-remote-area-selection-badges';
    Object.assign(badge.style, {
      left: `${Math.max(24, right - 6)}px`,
      top: `${Math.max(4, top + 4)}px`,
      transform: 'translateX(-100%)',
    });
    this.applyRemotePresenceAvatarScale(
      badge,
      this.getRemotePresenceVisualScale(targets[0])
    );
    badge.dataset.dvRemoteAreaSelectionOwner = this._remoteCellCaretPortalOwner;
    badge.append(this.createRemotePresenceAvatar(selection));

    this.ownerDocument.body.append(overlay, badge);
  }

  private applyRemotePresenceAvatarScale(element: HTMLElement, scale: number) {
    element.style.setProperty(
      '--dv-remote-presence-avatar-size',
      `${20 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-overlap',
      `${-6 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-font-size',
      `${9 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-ring-inset',
      `${-2 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-ring-width',
      `${2 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-shadow-y',
      `${1 * scale}px`
    );
    element.style.setProperty(
      '--dv-remote-presence-avatar-shadow-blur',
      `${4 * scale}px`
    );
  }

  private createRemotePresenceAvatar(presence: {
    color: string;
    userAvatar?: string | null;
    userName?: string;
  }) {
    const avatar = this.ownerDocument.createElement('div');
    avatar.className =
      'dv-remote-presence-avatar dv-remote-cell-presence-avatar';
    avatar.style.setProperty(
      '--dv-remote-presence-avatar-color',
      presence.color
    );
    avatar.title = remoteCellPresenceLabel(presence) ?? 'User';
    const rawAvatarUrl = presence.userAvatar;
    const content = this.ownerDocument.createElement('div');
    content.className =
      'dv-remote-presence-avatar-content dv-remote-cell-presence-avatar-content';
    if (isRenderableRemotePresenceAvatarUrl(rawAvatarUrl)) {
      const image = this.ownerDocument.createElement('img');
      image.alt = presence.userName ?? 'User';
      image.src = rawAvatarUrl;
      content.append(image);
    } else {
      const fallback = this.ownerDocument.createElement('span');
      fallback.textContent = this.getPresenceInitials(presence.userName);
      content.append(fallback);
    }
    avatar.append(content);
    return avatar;
  }

  private getRemotePresenceVisualScale(target: HTMLElement | undefined) {
    if (!target) {
      return 1;
    }
    const rect = target.getBoundingClientRect();
    const widthScale =
      target.offsetWidth > 0 && rect.width > 0
        ? rect.width / target.offsetWidth
        : 1;
    const heightScale =
      target.offsetHeight > 0 && rect.height > 0
        ? rect.height / target.offsetHeight
        : widthScale;
    const scale = widthScale > 0 ? widthScale : heightScale;
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  private renderRemoteCellPresenceBadges(
    target: HTMLElement,
    presences: DataViewRemoteCellPresence[]
  ) {
    if (getComputedStyle(target).position === 'static') {
      target.style.position = 'relative';
      target.dataset.dvCellPresencePositionAdjusted = 'true';
    }

    presences.forEach(presence => {
      if (presence.mode === 'editing' && presence.caretOffset != null) {
        this.renderRemoteCellCaretPresence(target, presence);
      }
    });

    const badges = document.createElement('div');
    badges.className = 'dv-remote-cell-presence-badges';
    const cellType =
      target.dataset.columnType ??
      target.querySelector<HTMLElement>('[data-column-type]')?.dataset
        .columnType;
    badges.dataset.placement = cellType === 'number' ? 'top-left' : 'top-right';
    presences.forEach(presence => {
      badges.append(this.createRemotePresenceAvatar(presence));
    });
    target.append(badges);
  }

  private renderRemoteCellCaretPresence(
    target: HTMLElement,
    presence: DataViewRemoteCellPresence
  ) {
    const rect = this.getRemoteCellCaretRect(target, presence);
    if (!rect) {
      return false;
    }

    const marker = document.createElement('div');
    marker.className = 'dv-remote-cell-caret-presence';
    marker.dataset.dvRemoteCellCaretOwner = this._remoteCellCaretPortalOwner;
    marker.style.position = 'fixed';
    marker.style.left = `${rect.left}px`;
    marker.style.top = `${rect.top}px`;
    marker.style.height = `${rect.height}px`;
    marker.style.width = '2px';
    marker.style.zIndex = '10000';
    marker.style.pointerEvents = 'none';
    marker.style.setProperty('--dv-remote-cell-caret-color', presence.color);

    const line = document.createElement('div');
    line.className = 'dv-remote-cell-caret-line';
    line.style.height = `${rect.height}px`;
    line.style.width = '2px';
    line.style.minHeight = '16px';
    line.style.borderRadius = '999px';
    line.style.background = presence.color;
    line.style.boxShadow = '0 0 0 1px rgb(255 255 255 / 75%)';
    marker.append(line);

    const caretLabel = remoteCellPresenceLabel(presence);
    if (caretLabel) {
      const label = document.createElement('div');
      label.className = 'dv-remote-cell-caret-label';
      Object.assign(
        label.style,
        createRemotePresenceCursorLabelStyle(presence.color, {
          placement: 'below',
        })
      );
      label.textContent = caretLabel;
      marker.append(label);
    }

    this.ownerDocument.body.append(marker);
    return true;
  }

  private getRemoteCellCaretRect(
    target: HTMLElement,
    presence: DataViewRemoteCellPresence
  ) {
    const richText = target.querySelector('rich-text') as
      | (HTMLElement & {
          inlineEditor?: {
            isValidInlineRange?: (range: {
              index: number;
              length: number;
            }) => boolean;
            toDomRange?: (range: { index: number; length: number }) => Range;
          } | null;
        })
      | null;
    const inlineEditor = richText?.inlineEditor;
    if (!inlineEditor?.toDomRange || presence.caretOffset == null) {
      return null;
    }

    const inlineRange = {
      index: presence.caretOffset,
      length: presence.caretLength ?? 0,
    };
    if (
      inlineEditor.isValidInlineRange &&
      !inlineEditor.isValidInlineRange(inlineRange)
    ) {
      return null;
    }

    const range = inlineEditor.toDomRange({ ...inlineRange, length: 0 });
    const rangeRect = Array.from(range.getClientRects()).at(-1);
    const rect =
      rangeRect && rangeRect.width + rangeRect.height > 0
        ? rangeRect
        : range.getBoundingClientRect();
    if (!rect || rect.width + rect.height === 0) {
      return null;
    }

    const targetRect = target.getBoundingClientRect();
    const left = Math.max(
      targetRect.left + 4,
      Math.min(rect.left, targetRect.right - 4)
    );
    const top = Math.max(targetRect.top + 2, rect.top);
    const height = Math.max(16, rect.height || 16);
    return { height, left, top };
  }

  private getPresenceInitials(name: string | undefined) {
    const value = name?.trim();
    if (!value) {
      return 'U';
    }
    const parts = value.split(/\s+/).filter(Boolean);
    const initials =
      parts.length > 1
        ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`
        : value.slice(0, 2);
    return initials.toUpperCase();
  }

  private escapeSelectorValue(value: string) {
    return globalThis.CSS?.escape(value) ?? value.replace(/["\\]/g, '\\$&');
  }

  override render() {
    return measureGfxPerformance(
      this.logic.config.performanceScope,
      'database-view-render',
      () => this.renderDataView(),
      () => this.logic.config.dataSource.rowCount$.value
    );
  }

  private renderDataView() {
    const containerClass = classMap({
      'toolbar-hover-container': true,
      'data-view-root': true,
      'prevent-reference-popup': true,
    });
    const currentView = this.logic.currentView$.value;
    const bulkState = this.logic.config.dataSource.bulkState$?.value;
    if (!currentView) {
      return;
    }
    const rowCounter = databaseRowCounter(
      this.logic.config.dataSource.rowCount$.value
    );
    const numberFormat = new Intl.NumberFormat();
    const shouldRenderHeader =
      this.logic.config.renderHeaderWidget !== false &&
      !!this.logic.config.headerWidget;
    const shouldRenderFooter =
      this.logic.config.renderFooterWidget !== false &&
      !!this.logic.config.footerWidget;
    const footerProgress = this.logic.config.footerProgress$?.value;
    return html`
      <div class="${containerClass}" data-data-view-frame="true">
        ${shouldRenderHeader
          ? html`
              <div data-data-view-frame-top="true">
                ${renderUniLit(this.logic.config.headerWidget, {
                  dataViewLogic: currentView,
                })}
              </div>
            `
          : nothing}
        ${bulkState
          ? html`<div
              role="status"
              aria-live="polite"
              style="padding:6px 12px;color:var(--affine-text-secondary-color)"
            >
              ${dataViewI18nT(`database.bulk.${bulkState.status}`, {
                count: bulkState.confirmedCount,
              })}
              ${bulkState.error
                ? dataViewI18nT(`database.bulk.failure.${bulkState.error.code}`)
                : nothing}
            </div>`
          : nothing}
        <div data-data-view-frame-body="true">
          ${renderUniLit(currentView.renderer, {
            logic: currentView,
          })}
        </div>
        ${rowCounter
          ? html`<div
              role="status"
              data-data-view-row-counter="true"
              style="padding:6px 12px;color:var(--affine-text-secondary-color)"
            >
              ${dataViewI18nT(
                rowCounter.overLimit
                  ? 'database.limit.rowsOverLimit'
                  : 'database.limit.rowCounter',
                {
                  count: numberFormat.format(rowCounter.count),
                  limit: numberFormat.format(rowCounter.limit),
                }
              )}
            </div>`
          : nothing}
        ${renderDataViewFooterProgress(footerProgress)}
        ${shouldRenderFooter
          ? html`
              <div data-data-view-frame-footer="true">
                ${renderUniLit(this.logic.config.footerWidget, {
                  dataViewLogic: currentView,
                })}
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-renderer': DataViewRootUI;
  }
}
